import {
  AlertTriangle,
  ArrowDownAZ,
  Bot,
  Clock,
  File,
  FileText,
  Link2,
  ListFilter,
  Plus,
  Search,
  Sparkles,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  ContentCard,
  ContentListSkeleton,
  CreateContentModal,
  EditContentModal,
  EmptyState,
  Input,
  Modal,
  PageLayout,
  Pagination,
  PreviewContentModal,
  Select,
} from "../components";
import type { ContentEntry } from "../components/cards/ContentCard";
import ICPAnalysisDisplay from "../components/empathy/ICPAnalysisDisplay";
import {
  HEADER_PRIMARY_BUTTON,
  HEADER_SECONDARY_BUTTON,
} from "../components/layout/headerActionStyles";
import ViralResearchModal from "../components/modals/ViralResearchModal";
import { useActiveContext } from "../contexts/ActiveContextContext";
import { useTranslation } from "../contexts/LanguageContext";
import { useContentLibrary } from "../hooks/useContentLibrary";
import postsService from "../services/postsService";
import { showToastError, showToastSuccess } from "../shared/alertService";
import apiService from "../shared/apiService";
import { API_ENDPOINTS } from "../shared/constants";

const ContentList: React.FC = () => {
  const { t } = useTranslation();
  const {
    contentLibrary,
    loading,
    total,
    countsByType,
    loadContentLibrary,
    deleteContent,
  } = useContentLibrary({ autoLoad: false });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [contentToDelete, setContentToDelete] = useState<ContentEntry | null>(
    null,
  );
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState<ContentEntry | null>(
    null,
  );
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editContent, setEditContent] = useState<ContentEntry | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [showIcpAnalysis, setShowIcpAnalysis] = useState(false);
  const [icpAnalysis] = useState<any>(null);
  const [showViralResearchModal, setShowViralResearchModal] = useState(false);

  // Estados para busca e filtros
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"date" | "name" | "type">("date");

  // Pagina atual sincronizada com o servidor
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8); // Grid fica melhor com multiplos de 3 ou 4

  // Track context to reset state synchronously and avoid flicker/inconsistency
  const { activeContext } = useActiveContext();
  const contextId = `${activeContext.type}-${activeContext.organization?.id || ""}-${activeContext.clientCompany?.id || ""}`;
  const [lastContextId, setLastContextId] = useState(contextId);

  if (contextId !== lastContextId) {
    setSearchQuery("");
    setFilterType("all");
    setCurrentPage(1);
    setLastContextId(contextId);
  }

  const handleDelete = async (content: ContentEntry) => {
    try {
      await deleteContent(content.id);
      showToastSuccess(t("Conteúdo excluído com sucesso!"));
      setDeleteModalOpen(false);
      setContentToDelete(null);
    } catch (err: any) {
      showToastError(err.message || t("Erro ao excluir conteúdo."));
    }
  };

  const openDeleteModal = (content: ContentEntry) => {
    setContentToDelete(content);
    setDeleteModalOpen(true);
  };

  const handleCreate = async (data: {
    content_name: string;
    content_type: "url" | "text" | "prompt" | "file";
    content_data: string;
    platforms: string[];
    business_type: string;
  }) => {
    setCreateLoading(true);
    try {
      const responseData = await apiService.post(
        API_ENDPOINTS.CONTENT.PROCESS_NAMED,
        data,
      );
      showToastSuccess(
        responseData.message || t("Conteúdo criado com sucesso!"),
      );
      setCreateModalOpen(false);
      await loadContentLibrary(currentPage, itemsPerPage);
    } catch (err: any) {
      throw err;
    } finally {
      setCreateLoading(false);
    }
  };

  // Filtrar e ordenar conteúdos
  const filteredAndSortedContents = useMemo(() => {
    let filtered = [...contentLibrary];

    // Filtro por busca
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (content) =>
          content.name.toLowerCase().includes(query) ||
          content.original_data?.toLowerCase().includes(query) ||
          content.business_type?.toLowerCase().includes(query),
      );
    }

    // Filtro por tipo
    if (filterType !== "all") {
      filtered = filtered.filter((content) => content.type === filterType);
    }

    // Ordenação
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "type":
          return a.type.localeCompare(b.type);
        case "date":
        default:
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
      }
    });

    return filtered;
  }, [contentLibrary, searchQuery, filterType, sortBy]);

  // Paginação
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const paginatedContents = contentLibrary;

  // Carregar dados quando a página mudar
  useEffect(() => {
    loadContentLibrary(currentPage, itemsPerPage);
  }, [currentPage, itemsPerPage, loadContentLibrary]);

  // Resetar para primeira página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterType, sortBy]);

  // Estatísticas de filtros
  // Totais da biblioteca inteira vêm do backend (total + counts_by_type); antes eram contados
  // só sobre os itens carregados e alternavam entre 8 (uma página) e 20 (tudo).
  const filterStats = useMemo(() => {
    const filtered = filteredAndSortedContents.length;
    const byType = countsByType ?? {
      url: contentLibrary.filter((c) => c.type === "url").length,
      text: contentLibrary.filter((c) => c.type === "text").length,
      prompt: contentLibrary.filter((c) => c.type === "prompt").length,
      file: contentLibrary.filter((c) => c.type === "file").length,
    };
    return { total, filtered, byType };
  }, [contentLibrary, filteredAndSortedContents, countsByType, total]);

  return (
    <PageLayout
      title={t("Gerenciar Conteúdos")}
      subtitle={t(
        "Visualize, organize e gerencie sua biblioteca de conteúdos criados.",
      )}
      containerSize="full"
      showContainer={true}
      padding="none"
      className="w-full px-2 py-4"
      loading={loading}
      skeleton={<ContentListSkeleton />}
      actions={
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowViralResearchModal(true)}
            variant="outline"
            size="sm"
            className={HEADER_SECONDARY_BUTTON}
            icon={<Sparkles className="h-3.5 w-3.5 text-indigo-600" />}
          >
            <span className="hidden sm:inline text-xs">
              {t("Deep Research")}
            </span>
            <span className="sm:hidden text-xs">{t("Viral")}</span>
          </Button>
          <Button
            onClick={() => setCreateModalOpen(true)}
            variant="primary"
            size="sm"
            className={HEADER_PRIMARY_BUTTON}
            icon={<Plus className="h-3.5 w-3.5" />}
          >
            <span className="hidden sm:inline text-xs">
              {t("Criar Conteúdo")}
            </span>
            <span className="sm:hidden text-xs">{t("Criar")}</span>
          </Button>
        </div>
      }
    >
      {/* Filter Bar - full-bleed sticky bar, mesmo padrão de "Postagens" */}
      <div className="sticky top-0 z-30 -mt-4 -mx-2 pl-4 pr-2 xl:pl-5 xl:pr-3 py-1.5 bg-white/95 backdrop-blur-md border-b border-purple-200 flex items-center justify-between">
        <div className="w-full">
          <div className="flex flex-row flex-wrap gap-2 sm:gap-2.5 items-center">
            {/* Busca - flexível, quebra linha quando necessário */}
            <div className="flex-1 min-w-[200px] sm:min-w-[250px]">
              <Input
                type="text"
                placeholder={t("Buscar conteúdos...")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                inputSize="sm"
                icon={<Search className="h-4 w-4 text-gray-400" />}
                className="w-full h-8! sm:h-9!"
              />
            </div>

            {/* Filtro por Tipo */}
            <div className="w-[170px] sm:w-[215px] shrink-0">
              <Select
                value={filterType}
                onChange={setFilterType}
                showSelectedCount
                options={[
                  {
                    value: "all",
                    label: t("Todos"),
                    icon: <ListFilter />,
                    count: filterStats.total,
                  },
                  {
                    value: "url",
                    label: t("URLs"),
                    icon: <Link2 />,
                    count: filterStats.byType.url,
                  },
                  {
                    value: "text",
                    label: t("Textos"),
                    icon: <FileText />,
                    count: filterStats.byType.text,
                  },
                  {
                    value: "prompt",
                    label: t("Prompts IA"),
                    icon: <Bot />,
                    count: filterStats.byType.prompt,
                  },
                  {
                    value: "file",
                    label: t("Arquivos"),
                    icon: <File />,
                    count: filterStats.byType.file,
                  },
                ]}
                size="sm"
              />
            </div>

            {/* Filtro Ordenar */}
            <div className="w-[150px] sm:w-[185px] shrink-0">
              <Select
                value={sortBy}
                onChange={(value) =>
                  setSortBy(value as "date" | "name" | "type")
                }
                options={[
                  {
                    value: "date",
                    label: t("Mais recentes"),
                    icon: <Clock />,
                  },
                  {
                    value: "name",
                    label: t("Nome (A-Z)"),
                    icon: <ArrowDownAZ />,
                  },
                  {
                    value: "type",
                    label: t("Por tipo"),
                    icon: <ListFilter />,
                  },
                ]}
                size="sm"
              />
            </div>
          </div>

          {/* Indicador discreto de resultados */}
          {contentLibrary.length > 0 &&
            (filterType !== "all" || searchQuery) && (
              <div className="mt-3 pt-3 border-t border-purple-300 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between text-sm text-gray-600">
                <span>
                  {t("Exibindo")}{" "}
                  <strong className="text-gray-900">
                    {filterStats.filtered}
                  </strong>{" "}
                  {t("de")} {filterStats.total} {t("conteúdos")}
                </span>
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setFilterType("all");
                  }}
                  className="text-purple-600 hover:text-purple-700 font-medium transition-colors text-left sm:text-right"
                >
                  {t("Limpar filtros")}
                </button>
              </div>
            )}
        </div>
      </div>{" "}
      {/* Fim da Filter Bar */}
      <div className="w-full flex-1 mt-2!">
        {contentLibrary.length === 0 ? (
          <div className="bg-white/50 border border-purple-200/50 rounded-3xl py-12 sm:py-20 shadow-xs flex flex-col items-center justify-center">
            <EmptyState
              icon={
                <FileText className="h-16 w-16 sm:h-20 sm:w-20 text-gray-400" />
              }
              title={t("Nenhum conteúdo salvo")}
              description={t(
                "Crie seu primeiro conteúdo para começar a gerar posts e calendários de publicação nas redes sociais.",
              )}
              action={
                <Button
                  onClick={() => setCreateModalOpen(true)}
                  variant="primary"
                  icon={<Plus className="h-4 w-4" />}
                  size="md"
                >
                  {t("Criar Primeiro Conteúdo")}
                </Button>
              }
            />
          </div>
        ) : filteredAndSortedContents.length === 0 ? (
          <div className="bg-white/50 border border-purple-200/50 rounded-3xl py-12 sm:py-20 shadow-xs flex flex-col items-center justify-center mt-6">
            <EmptyState
              icon={
                <Search className="h-16 w-16 sm:h-20 sm:w-20 text-gray-400" />
              }
              title={t("Nenhum conteúdo encontrado")}
              description={`${t("Não foram encontrados conteúdos com os filtros aplicados.")}${searchQuery ? ` ${t("Busca:")} "${searchQuery}"` : ""}`}
              action={
                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <Button
                    onClick={() => {
                      setSearchQuery("");
                      setFilterType("all");
                      setSortBy("date");
                    }}
                    variant="secondary"
                    size="md"
                  >
                    {t("Limpar Filtros")}
                  </Button>
                  <Button
                    onClick={() => setCreateModalOpen(true)}
                    variant="primary"
                    icon={<Plus className="h-4 w-4" />}
                    size="md"
                  >
                    {t("Criar Novo Conteúdo")}
                  </Button>
                </div>
              }
            />
          </div>
        ) : (
          <>
            {/* Os filtros duplicados com "Por tipo: URL 1" foram removidos já que implementamos os pills na Action Bar que cumprem essa função com um visual mais harmônico */}

            {/* Grid de conteúdos - Utilizando minmax auto-fill para preencher 100% dos espaços em qualquer tela */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
              {paginatedContents.map((content: ContentEntry, index: number) => (
                <div
                  key={`content-${content.id}-${index}-${content.created_at}`}
                  className="w-full"
                >
                  <ContentCard
                    content={content}
                    onDelete={() => openDeleteModal(content)}
                    onPreview={() => {
                      setPreviewContent(content);
                      setPreviewModalOpen(true);
                    }}
                    onEdit={() => {
                      setEditContent(content);
                      setEditModalOpen(true);
                    }}
                  />
                </div>
              ))}
            </div>
          </>
        )}

        {/* Footer com paginação isolada num container clean */}
        {filteredAndSortedContents.length > 0 && (
          <div className="mt-6 sm:mt-10 flex justify-center w-full">
            <div className="bg-white/80 backdrop-blur-md border border-purple-200/50 shadow-xs rounded-xl py-2.5 px-6 w-full max-w-4xl mx-auto hover:shadow-md transition-shadow duration-300">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={total}
                itemsPerPage={itemsPerPage}
                onPageChange={(page: number) => {
                  setCurrentPage(page);
                  // Scroll suave para o topo
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                showInfo={true}
                maxVisiblePages={5}
              />
            </div>
          </div>
        )}
      </div>{" "}
      {/* Fim do Wrapper Flex-1 */}
      {/* Modal de confirmação para exclusão */}
      {deleteModalOpen && contentToDelete && (
        <Modal
          open={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setContentToDelete(null);
          }}
          title={t("Confirmar Exclusão")}
          subtitle={t(
            "Esta ação removerá permanentemente o material da biblioteca",
          )}
          icon={<AlertTriangle className="h-5 w-5" />}
          headerVariant="gradient"
          maxWidth="max-w-md"
          footer={
            <div className="flex justify-end gap-3 w-full">
              <Button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setContentToDelete(null);
                }}
                size="sm"
                variant="secondary"
                className="border-purple-200"
              >
                {t("Manter Conteúdo")}
              </Button>
              <Button
                onClick={() => handleDelete(contentToDelete)}
                size="sm"
                variant="primary"
                className="bg-red-600 hover:bg-red-700 shadow-lg shadow-red-500/20"
              >
                {t("Sim, Excluir")}
              </Button>
            </div>
          }
        >
          <div className="text-center space-y-4 p-2">
            <div className="w-16 h-16 bg-red-50 text-red-600 rounded-3xl flex items-center justify-center mx-auto border border-red-100 mb-2">
              <AlertTriangle className="h-8 w-8" />
            </div>
            <p className="text-gray-700 text-base">
              {t("Tem certeza que deseja excluir o conteúdo")} <br />
              <strong className="text-gray-900 text-lg">
                "{contentToDelete.name}"
              </strong>
              ?
            </p>
            <p className="text-[11px] font-bold uppercase tracking-widest text-red-500 bg-red-50 inline-block px-4 py-1.5 rounded-full border border-red-100">
              {t("Atenção: Esta ação é irreversível")}
            </p>
          </div>
        </Modal>
      )}
      <PreviewContentModal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        content={previewContent}
      />
      <EditContentModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        content={editContent}
        loading={editLoading}
        onSave={async (data) => {
          if (!editContent) return;
          setEditLoading(true);
          try {
            await postsService.editContent(editContent.id, data);
            showToastSuccess(t("Conteúdo editado com sucesso!"));
            setEditModalOpen(false);
            setEditContent(null);
            loadContentLibrary(currentPage, itemsPerPage);
          } catch (err: any) {
            showToastError(err.message || t("Erro ao editar conteúdo."));
          } finally {
            setEditLoading(false);
          }
        }}
      />
      <CreateContentModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        loading={createLoading}
        onCreate={handleCreate}
        onSuccess={() => loadContentLibrary(currentPage, itemsPerPage)}
      />
      {/* Modal de Análise de ICP */}
      {showIcpAnalysis && icpAnalysis && (
        <Modal
          open={showIcpAnalysis}
          onClose={() => setShowIcpAnalysis(false)}
          title={t("🎯 Análise Automática de ICP")}
          maxWidth="max-w-6xl"
          footer={
            <div className="flex justify-end space-x-3">
              <Button
                onClick={() => setShowIcpAnalysis(false)}
                variant="secondary"
              >
                {t("Fechar")}
              </Button>
              <Button
                onClick={() => {
                  setShowIcpAnalysis(false);
                  showToastSuccess(t("Recomendações aplicadas!"));
                }}
                variant="primary"
              >
                {t("Aplicar Recomendações")}
              </Button>
            </div>
          }
        >
          <ICPAnalysisDisplay analysis={icpAnalysis} />
        </Modal>
      )}
      <ViralResearchModal
        open={showViralResearchModal}
        onClose={() => setShowViralResearchModal(false)}
        onUseContent={(content) => {
          setCreateModalOpen(true);
          localStorage.setItem("viral_research_content_library", content);
          window.dispatchEvent(
            new CustomEvent("viral_research_content_library_ready", {
              detail: content,
            }),
          );
        }}
      />
    </PageLayout>
  );
};

export default ContentList;
